import { Delete } from '@mui/icons-material';
import { Button, MenuItem } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React from 'react';

import { StyledSelect } from '../StyledSelect/StyledSelect';
import { AdministrationRoute, DrugReference } from 'FRONTEND/utils/backendApi/drugReferencesApi';
import i18n from 'FRONTEND/utils/i18n';

type ColumnParams = {
  isEditing: boolean;
  editRight: boolean;
  deleteRight: boolean;
  choices: ({ value: string; label: string } | string)[];
  handleEditDrugReference: (params: { drugReferenceId: string; preferredRoute: string }) => void;
  setReferenceToDelete: (id: string) => void;
  setIsDeleteModalOpen: (open: boolean) => void;
  userLocale: 'ca' | 'fr' | 'de' | 'en' | 'es'
};

export const getDrugReferencesColumns = ({ isEditing, editRight, deleteRight, choices, handleEditDrugReference, setReferenceToDelete, setIsDeleteModalOpen, userLocale }: ColumnParams): GridColDef[] => [
  {
    field: 'name',
    headerName: i18n._('Commercial name'),
    flex: 1,
    type: 'string',
    valueGetter: (_value, row: DrugReference) => row?.commercial_name,
  },
  {
    field: 'activePrinciple',
    headerName: i18n._('Active principle'),
    flex: 1,
    type: 'string',
    valueGetter: (_value, row: DrugReference) => row?.level5?.title || '—',
  },
  {
    field: 'classification',
    headerName: i18n._('Classification'),
    flex: 1,
    type: 'string',
    valueGetter: (_value, row: DrugReference) => row?.level2?.title || '—',
  },
  {
    field: 'quantityPerVolume',
    headerName: i18n._('Quantity per volume'),
    flex: 1,
    type: 'string',
    maxWidth: 150,
    valueGetter: (_value, row: DrugReference) => row.quantity_per_volume || '—',
  },
  {
    field: 'administrationRoute',
    headerName: i18n._('Administration route'),
    flex: 1,
    type: 'string',
    maxWidth: 200,
    renderCell: (params) => {
      const row: DrugReference = params.row;
      const route: AdministrationRoute = row?.preferred_administration_route || '';

      return (
        <StyledSelect
          variant='outlined'
          size='small'
          fullWidth
          value={route}
          disabled={isEditing || !editRight}
          sx={{
            borderRadius: '50vh',
          }}
          MenuProps={{
            sx: {
              gap: 1,
            },
            slotProps: {
              paper: {
                sx: {
                  border: 1,
                  borderColor: (theme) => theme.palette.inputFieldGlow.main,
                  borderRadius: '4px',
                },
              },
            },
          }}
        >
          <MenuItem
            sx={{
              paddingY: 2,
            }}
            value=''
            onClick={() => handleEditDrugReference({ drugReferenceId: row.id, preferredRoute: '' })}
          />

          {choices?.map((adminRoute) => {
            // some Choices are still in the string only format, this ensures some type safety at least
            // TODO: handle the case where we have translations
            const value = typeof adminRoute === 'string' ? adminRoute : adminRoute.value;
            const label = typeof adminRoute === 'string' ? adminRoute : typeof adminRoute.label === 'string' ? adminRoute.label : adminRoute.label[userLocale];
            return (
              <MenuItem
                key={value}
                value={value}
                onClick={() => handleEditDrugReference({ drugReferenceId: row.id, preferredRoute: value })}
              >
                {label}
              </MenuItem>
            );
          },
          )}
        </StyledSelect>
      );
    },
  },
  {
    field: 'delete',
    headerName: i18n._('Delete'),
    flex: 1,
    sortable: false,
    filterable: false,
    maxWidth: 100,
    renderCell: (params) => (
      <Button
        variant='contained'
        size='small'
        color='error'
        fullWidth
        onClick={() => {
          setReferenceToDelete(params.row.id);
          setIsDeleteModalOpen(true);
        }}
        disabled={!deleteRight}
      >
        <Delete />
      </Button>
    ),
  },
];
